import React from 'react';

import { INITIAL_DATA_CHUNK_SIZE } from '@clearverify/constants';

import {
  InboxItemsListWhere,
  InboxItemsScrollableListQueryVariables,
  InboxItemType,
} from 'src/api';
import { getInboxFormFilterObject, isInboxFormFilterEmpty } from 'src/components/inboxFilter/utils';
import { prepareInboxSearchFilter } from 'src/utils/inboxItem';

import { useSearchQueryVariables } from '../common';
import { useTestVerificationRequestsRoute } from '../testVerificationRequests';
import { useCurrentUserPermissions } from '../user';

import { useInboxFilter } from './useInboxFilter';

export const useInboxItemsQueryVariables = (): InboxItemsScrollableListQueryVariables => {
  const { filterFormValues } = useInboxFilter();
  const { isTestRequestsRoute } = useTestVerificationRequestsRoute();
  const searchQueryFilters = useSearchQueryVariables(searchValue =>
    prepareInboxSearchFilter(searchValue),
  );

  const userPermissions = useCurrentUserPermissions();

  const inboxFormFilter = React.useMemo((): InboxItemsListWhere => {
    if (isInboxFormFilterEmpty(filterFormValues)) return {};
    return getInboxFormFilterObject(filterFormValues);
  }, [filterFormValues]);

  const inboxItemsFilter = React.useMemo((): InboxItemsListWhere => {
    // Since the Test Verification Request is visible only to the Platform Admins,
    // we hide the excessive complexity from other roles
    if (userPermissions.isPlatformAdmin) {
      if (isTestRequestsRoute) {
        return {
          type: { equals: InboxItemType.TestVerificationRequest },
          ...searchQueryFilters,
        };
      }

      return {
        type: { not: { equals: InboxItemType.TestVerificationRequest } },
        ...inboxFormFilter,
        ...searchQueryFilters,
      };
    }

    return {
      ...inboxFormFilter,
      ...searchQueryFilters,
    };
  }, [inboxFormFilter, searchQueryFilters, isTestRequestsRoute, userPermissions.isPlatformAdmin]);

  return {
    where: inboxItemsFilter,
    ...defaultVariables,
  };
};

const defaultVariables: InboxItemsScrollableListQueryVariables = {
  take: INITIAL_DATA_CHUNK_SIZE,
  skip: 0,
};
